import { PropsWithChildren, useLayoutEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Toolbar,
} from "@mui/material";
import { SortableList, Typography } from "@platform/design-system";

import { Carrier } from "../types";

import type { Theme } from "@mui/material/styles";

type TemplateOption = {
  name: string;
  url: string;
};

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  onGeneratePresentation: (selected: string, orderedCarriers: string[]) => void;
  isGeneratingPresentation: boolean;
  templates: TemplateOption[];
  carriers: Carrier[];
  onPreview: (templateName: string) => void;
};

const GeneratePresentationDrawer = ({
  isOpen,
  closeModal,
  onGeneratePresentation,
  isGeneratingPresentation,
  templates,
  carriers,
  onPreview,
}: Props): React.JSX.Element => {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [orderedCarriers, setOrderedCarriers] = useState<string[]>([]);

  useLayoutEffect(() => {
    if (isOpen) {
      setOrderedCarriers(carriers.map((c) => c.name));
    }
  }, [isOpen, carriers]);

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTemplate(event.target.value);
  };

  const handleGenerate = () => {
    onGeneratePresentation(selectedTemplate, orderedCarriers);
  };

  // Reset selected template when the modal opens. This ensures the template
  // is reset when the modal is closed and opened again.
  useLayoutEffect(() => {
    if (isOpen) {
      setSelectedTemplate("");
    }
  }, [isOpen]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={isGeneratingPresentation ? undefined : closeModal}
    >
      <Box maxWidth={500} height="100%" display="flex" flexDirection="column">
        <DrawerToolbar>
          <Typography variant="header-small">Generate Presentation</Typography>
        </DrawerToolbar>
        <Stack padding={2} flex={1} spacing={3}>
          <DrawerSection
            title="Select Template"
            description="Select a template to generate from. Please note, this is the only opportunity to choose a template for this presentation."
          >
            <RadioGroup value={selectedTemplate} onChange={handleSelect}>
              {templates.map((template) => (
                <TemplateRow key={template.name}>
                  <FormControlLabel
                    value={template.name}
                    control={<Radio />}
                    label={<Typography>{template.name}</Typography>}
                  />
                  <PreviewButton
                    variant="text"
                    onClick={() => onPreview(template.url)}
                  >
                    Preview
                  </PreviewButton>
                </TemplateRow>
              ))}
            </RadioGroup>
          </DrawerSection>
          <DrawerSection
            title="Sort Carriers"
            description="Sort the carriers for your presentation by clicking and dragging. This action will arrange the columns in the spreadsheet from left to right based on the sort level from top to bottom."
          >
            <SortableList
              items={orderedCarriers}
              onOrderChange={setOrderedCarriers}
              renderContent={(item) => (
                <ListItemText primary={<Typography>{item}</Typography>} />
              )}
            />
          </DrawerSection>
        </Stack>
        <DrawerToolbar bottom>
          <Button
            onClick={closeModal}
            variant="text"
            disabled={isGeneratingPresentation}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={!selectedTemplate || isGeneratingPresentation}
          >
            {isGeneratingPresentation ? "Generating..." : "Generate"}
          </Button>
        </DrawerToolbar>
      </Box>
    </Drawer>
  );
};

const DrawerSection = (
  props: PropsWithChildren<{ title: string; description: string }>,
) => (
  <Box
    component="section"
    sx={{
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Typography variant="header-small">{props.title}</Typography>
    <Typography
      variant="body-medium"
      color="textSecondary"
      sx={{
        marginTop: 1,
        marginBottom: 2,
      }}
    >
      {props.description}
    </Typography>
    {props.children}
  </Box>
);

const DrawerToolbar = styled(Toolbar)<{ bottom?: boolean }>(
  ({ theme, bottom = false }: { theme: Theme; bottom?: boolean }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: !bottom ? `1px solid ${theme.palette.divider}` : "none",
    borderTop: bottom ? `1px solid ${theme.palette.divider}` : "none",
    [theme.breakpoints.up("xs")]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  }),
);

const TemplateRow = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(1),
}));

const PreviewButton = styled(Button)(() => ({
  fontWeight: 500,
}));

export default GeneratePresentationDrawer;
